import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Campaign as NewsIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import AnnouncementDialog from './AnnouncementDialog';

const TYPE_COLORS = {
  announcement: { bg: 'rgba(44, 156, 108, 0.15)', color: '#4DC896' },
  alert: { bg: 'rgba(220, 38, 38, 0.15)', color: '#EF4444' },
  event: { bg: 'rgba(201, 168, 76, 0.15)', color: '#c9a84c' },
  promotion: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818CF8' },
};

const NewsSection = ({ isAdmin, t }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const limit = expanded ? 50 : 3;
      const res = await api.get(`/announcements?limit=${limit}`);
      setAnnouncements(res.data.announcements || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      // silent fail
    }
  }, [expanded]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const handleSave = async (data) => {
    try {
      if (editingAnnouncement) {
        await api.put(`/announcements/${editingAnnouncement.id}`, data);
      } else {
        await api.post('/announcements', data);
      }
      setDialogOpen(false);
      setEditingAnnouncement(null);
      fetchAnnouncements();
    } catch (err) {
      // silent fail
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('dashboard.confirmDeleteAnnouncement'))) return;
    try {
      await api.delete(`/announcements/${id}`);
      fetchAnnouncements();
    } catch (err) {
      // silent fail
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingAnnouncement(null);
    setDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NewsIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {t('dashboard.news')}
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ fontSize: '0.75rem' }}
          >
            {t('dashboard.createAnnouncement')}
          </Button>
        )}
      </Box>

      {announcements.length === 0 ? (
        <Typography sx={{ textAlign: 'center', py: 3, color: 'text.secondary', fontSize: '0.9rem' }}>
          {t('dashboard.noAnnouncements')}
        </Typography>
      ) : (
        <>
          {announcements.map((item) => {
            const typeStyle = TYPE_COLORS[item.type] || TYPE_COLORS.announcement;
            return (
              <Box
                key={item.id}
                sx={{
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                      <Chip
                        label={t(`dashboard.type_${item.type || 'announcement'}`)}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          bgcolor: typeStyle.bg,
                          color: typeStyle.color,
                          border: 'none',
                        }}
                      />
                      {item.end_date && (
                        <Typography sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                          {t('dashboard.expiresOn', { date: formatDate(item.end_date) })}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.8rem',
                        mt: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {item.content}
                    </Typography>
                    <Typography sx={{ color: 'text.disabled', fontSize: '0.7rem', mt: 0.5 }}>
                      {t('dashboard.postedBy', { author: item.author_name })} &middot; {formatDate(item.created_at)}
                    </Typography>
                  </Box>
                  {isAdmin && (
                    <Box sx={{ display: 'flex', ml: 1 }}>
                      <IconButton size="small" onClick={() => handleEdit(item)}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(item.id)}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}

          {total > 3 && (
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <CollapseIcon /> : <ExpandIcon />}
              sx={{ mt: 1, width: '100%', color: 'text.secondary' }}
            >
              {expanded ? t('dashboard.showLess') : t('dashboard.viewAllNews')}
            </Button>
          )}
        </>
      )}

      <AnnouncementDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingAnnouncement(null); }}
        onSave={handleSave}
        announcement={editingAnnouncement}
        t={t}
      />
    </Paper>
  );
};

export default NewsSection;
